import type { LipDetection } from '../tracking/faceLandmarker';
import {
  buildOnnxSemanticFaceMap,
  isOnnxSemanticParserReady,
  warmOnnxSemanticParser
} from './onnxFaceParser';
import { buildSemanticFaceMap, type SemanticFaceMap } from './semanticFaceMap';

export interface SemanticProviderOptions {
  timeoutMs?: number;
  mode?: 'balanced' | 'high';
}

const BALANCED_TIMEOUT_MS = 3500;
const HIGH_TIMEOUT_MS = 30000;

function timeout<T>(ms: number, value: T): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(value), ms);
  });
}

export async function buildBestSemanticFaceMap(
  source: ImageBitmap | HTMLCanvasElement,
  detection: LipDetection,
  options: SemanticProviderOptions = {}
): Promise<SemanticFaceMap> {
  const timeoutMs = options.timeoutMs
    ?? (options.mode === 'high' ? HIGH_TIMEOUT_MS : BALANCED_TIMEOUT_MS);
  if (options.mode !== 'high' && !isOnnxSemanticParserReady()) {
    warmOnnxSemanticParser();
    const fallback = await buildSemanticFaceMap(source, detection, { mode: 'fast' });
    fallback.quality.fallbacks = [
      ...fallback.quality.fallbacks,
      'face-parsing warming in background'
    ];
    return fallback;
  }
  const parsed = await Promise.race([
    buildOnnxSemanticFaceMap(source, detection, {
      requireReady: options.mode !== 'high'
    }),
    timeout<SemanticFaceMap | null>(timeoutMs, null)
  ]);
  if (parsed) return parsed;
  const fallback = await buildSemanticFaceMap(source, detection, {
    mode: options.mode === 'high' ? 'full' : 'fast'
  });
  if (timeoutMs < HIGH_TIMEOUT_MS) {
    fallback.quality.fallbacks = [
      ...fallback.quality.fallbacks,
      `face-parsing timeout ${timeoutMs}ms`
    ];
  }
  return fallback;
}
